import axios from 'axios';
import { LoginCredentials, RegisterData, TokenResponse } from '../types';

const API_BASE_URL = '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30s timeout for long-running audit tasks
  headers: {
    'Content-Type': 'application/json',
  },
});

// NOTE: Token is stored in localStorage for SPA convenience.
// For production, consider httpOnly cookies with CSRF protection
// to mitigate XSS token theft. See: https://cheatsheetseries.owasp.org/cheatsheets/JSON_Web_Token_for_Java_Cheat_Sheet.html#token-storage

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, null, {
            params: { refresh_token: refreshToken },
          });

          const { access_token, refresh_token } = response.data;
          localStorage.setItem('token', access_token);
          localStorage.setItem('refreshToken', refresh_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (credentials: LoginCredentials) =>
    api.post<TokenResponse>('/auth/login', credentials),

  register: (data: RegisterData) =>
    api.post('/auth/register', data),

  getMe: () => api.get('/auth/me'),

  refreshToken: (refreshToken: string) =>
    api.post<TokenResponse>('/auth/refresh', null, {
      params: { refresh_token: refreshToken },
    }),
};

// Papers API
export const papersApi = {
  upload: (file: File, title?: string, doi?: string, onProgress?: (percent: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (doi) formData.append('doi', doi);

    return api.post('/papers/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 120 seconds for large file uploads through frp
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
  },

  list: (skip = 0, limit = 20) =>
    api.get(`/papers/?skip=${skip}&limit=${limit}`),

  get: (id: string) => api.get(`/papers/${id}`),

  delete: (id: string) => api.delete(`/papers/${id}`),
};

// Audit API
export const auditApi = {
  createTask: (paperId: string, taskType: string) =>
    api.post('/audit/tasks', {
      paper_id: paperId,
      task_type: taskType,
    }),

  listTasks: (paperId?: string) =>
    api.get('/audit/tasks', {
      params: paperId ? { paper_id: paperId } : undefined,
    }),

  getTask: (id: string) => api.get(`/audit/tasks/${id}`),

  getTaskStatus: (id: string) => api.get(`/audit/tasks/${id}/status`),

  getReport: (id: string, format: string = 'html') =>
    api.post(`/audit/tasks/${id}/report`, { format }),
};

// Extraction API
export const extractionApi = {
  extract: (paperId: string) =>
    api.post(`/extraction/papers/${paperId}`),

  getMetadata: (paperId: string) =>
    api.get(`/extraction/papers/${paperId}/metadata`),

  getTables: (paperId: string) =>
    api.get(`/extraction/papers/${paperId}/tables`),
};

export default api;
